import React from "react"
import { hydrateRoot, createRoot } from "react-dom/client"
import { RouterProvider, createBrowserRouter } from "react-router-dom"
import { routes } from "./routes"
import { SNAPSHOT_ELEMENT_ID, setContentSnapshot } from "./lib/content"
import "./index.css"

/*
  Development only. A production page always ships an embedded content
  snapshot, but `pnpm dev` serves no prerendered HTML — so without this, the
  site renders empty for anyone working on it without the API running.

  The import is dynamic and guarded so the seed stays out of the production
  graph entirely; see the note on fallback() in lib/content.ts.
*/
if (import.meta.env.DEV && !document.getElementById(SNAPSHOT_ELEMENT_ID)) {
  void import("./lib/seed").then(({ SEED }) => {
    setContentSnapshot({
      caseStudies: SEED.caseStudies,
      posts: SEED.posts,
      settings: SEED.settings,
    })
  })
}

const router = createBrowserRouter(routes)
const container = document.getElementById("root")!

const app = (
  <React.StrictMode>
    <div id="app">
      <RouterProvider router={router} />
    </div>
  </React.StrictMode>
)

/*
  Prerendered routes ship real markup and are hydrated in place. Anything the
  build did not prerender — the dashboard shell, an unmatched URL served by the
  SPA fallback, and every page under `pnpm dev` — mounts fresh instead.

  The test is firstElementChild, not hasChildNodes(). The template ships
  `<div id="root"><!--app-html--></div>`, and a comment is a child node: in
  development, where nothing replaces that comment, hasChildNodes() answered
  true and sent an unrendered shell down the hydration path. React then found a
  comment where <div id="app"> should be and logged a hydration mismatch on
  every page load, which cost nothing at runtime (it re-rendered client-side)
  but buried any real mismatch in noise. firstElementChild ignores comments and
  whitespace, so it is true only when there is actual markup to adopt.
*/
if (container.firstElementChild) {
  hydrateRoot(container, app)
} else {
  createRoot(container).render(app)
}
